/**
 * RegistrationTaskSection.tsx - the registration a task of the reminder loop is about («Eintragung
 * Brokermandat nicht bestätigt», «Versicherer ohne Rückmeldung», a failed reminder), in the Aufgaben
 * peek: the insurer, the status in the api's words, the Vorgangs-ID, when it was reported and reminded,
 * and for a Brokermandat S5's decisions on the insurer's answer («Bestätigen» with a Beleg, «Ablehnen»,
 * «Rückfrage»), each for a caller with mandate.eingangskontrolle and only when the row allows it.
 */

import { useState } from 'react';
import { formatZurichDateDe } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { isNotFound, useRegistrationDecision, useTaskRegistration } from './mandate-registration-queries';
import { REGISTRATION_DECISION_KEY } from './MandateRegistrationPanel';
import { RegistrationAnswerForm, type RegistrationAnswerFormKind } from './RegistrationDecisionDialogs';
import { RegistrationReminderLines } from './RegistrationReminderLines';
import { registrationErrorText } from './registration-error-text';

const DECISIONS: ReadonlyArray<{ kind: Exclude<RegistrationAnswerFormKind, 'korrektur'>; label: string }> = [
  { kind: 'bestaetigen', label: 'Bestätigen' },
  { kind: 'ablehnen', label: 'Ablehnen' },
  { kind: 'rueckfrage', label: 'Rückfrage' },
];

export function RegistrationTaskSection({
  taskId,
  subjectType,
  subjectId,
}: {
  taskId: string;
  subjectType: 'person' | 'firma';
  subjectId: string;
}) {
  const reg = useTaskRegistration(taskId);
  const { can } = useEffectivePermissions();
  const canDecide = can(REGISTRATION_DECISION_KEY);
  const [open, setOpen] = useState<RegistrationAnswerFormKind | null>(null);
  const decide = useRegistrationDecision(reg.data?.mandate_id ?? '', { subjectType, subjectId });

  if (reg.isError) {
    if (isNotFound(reg.error)) return null;
    return (
      <p className="text-sm text-destructive" role="alert">
        {registrationErrorText(reg.error)}
      </p>
    );
  }
  const r = reg.data;
  if (!r) return null;
  const sent = r.sent_at ? formatZurichDateDe(r.sent_at) : null;
  // The answer decisions run on the Brokermandat registration (S5); an analysis row is closed on its panel.
  const decisions = r.prozess === 'p5_broker' && canDecide ? DECISIONS.filter((d) => r.actions[d.kind]) : [];

  return (
    <div className="flex flex-col gap-1 text-sm" data-testid="reg-task-section">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{r.carrier_name ?? r.carrier_uid}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{r.status_label}</span>
        <span className="text-xs text-muted-foreground">{r.vorgangs_ref}</span>
      </div>
      {sent && <span className="text-xs text-muted-foreground">Gemeldet: {sent}</span>}
      <RegistrationReminderLines row={r} />
      {decide.isError && (
        <p className="text-sm text-destructive" role="alert">
          {registrationErrorText(decide.error)}
        </p>
      )}
      {canDecide && (r.actions.versand_bestaetigen || r.actions.nicht_versendet) && (
        <div className="mt-1 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => decide.mutate({ action: 'versand_bestaetigt', registrationId: r.id })} data-testid="reg-versand-bestaetigt">
            Versand bestätigt
          </Button>
          <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => decide.mutate({ action: 'nicht_versendet', registrationId: r.id })} data-testid="reg-nicht-versendet">
            Nicht versendet
          </Button>
        </div>
      )}
      {decisions.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-2">
          {decisions.map((d) => (
            <Button key={d.kind} size="sm" variant="outline" onClick={() => setOpen(d.kind)} data-testid={`reg-${d.kind}`}>
              {d.label}
            </Button>
          ))}
        </div>
      )}
      {open && (
        <RegistrationAnswerForm
          key={open}
          kind={open}
          row={r}
          subjectType={subjectType}
          subjectId={subjectId}
          pending={decide.isPending}
          onSubmit={(d) => decide.mutate(d, { onSuccess: () => setOpen(null) })}
          onCancel={() => setOpen(null)}
        />
      )}
    </div>
  );
}
